(() => {
  const MSG_ALREADY_JOINED = "Already joined";
  const MSG_IP_LIMIT = "Participation limit reached";
  const MSG_NO_ACT = "This campaign is unavailable.";
  const MSG_NO_LOGIN = "Please enter a valid email address.";
  const MSG_SERVER_ERROR = "Something went wrong. Please try again.";
  const SPIN_DURATION_MS = 3000;
  const SPIN_TURNS = 3;

  // Calibrate to match the wheel artwork (pointer at top).
  // Degrees = clockwise rotation that brings that prize center under the pointer.
  const PRIZE_ANGLES = {
    first: 0,
    second: 290,
    third: 70,
    everyone: 210,
    none: 140
  };

  const PRICE_ID_MAP = {
    1065: "first",
    1064: "second",
    1063: "third",
    1062: "everyone"
  };

  const KLAVIYO_FORM_ID = "QUQ9YY";
  const instances = new Set();

  const trackSubscribe = (status, emailValue) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event_parameters: null });
    window.dataLayer.push({
      event: "ga4Event",
      event_name: "subscribe",
      event_parameters: {
        module: "gnr_subscription",
        status: status,
        email: emailValue || ""
      }
    });
  };

  const openDialog = (dialog) => {
    if (!dialog) return;
    if (typeof dialog.showModal === "function") {
      dialog.showModal();
    } else {
      dialog.setAttribute("open", "");
    }
  };

  const closeDialog = (dialog) => {
    if (!dialog) return;
    if (typeof dialog.close === "function") {
      dialog.close();
    } else {
      dialog.removeAttribute("open");
    }
  };

  const mapPriceId = (priceId) => {
    const id = Number(priceId);
    return PRICE_ID_MAP[id] || "none";
  };

  const getErrorMessage = (actStatus) => {
    switch (actStatus) {
      case "no_finish":
        return MSG_ALREADY_JOINED;
      case "no_finish_ip":
        return MSG_IP_LIMIT;
      case "no_act":
        return MSG_NO_ACT;
      case "no_login":
        return MSG_NO_LOGIN;
      default:
        return MSG_SERVER_ERROR;
    }
  };

  const pickField = (source, ...keys) => {
    if (!source || typeof source !== "object") return undefined;
    for (const key of keys) {
      if (source[key] !== undefined && source[key] !== null && source[key] !== "") {
        return source[key];
      }
    }
    return undefined;
  };

  const initWinnersTabs = (root) => {
    const tabs = [...root.querySelectorAll("[data-gnr-winner-tab]")];
    const panels = [...root.querySelectorAll("[data-gnr-winner-panel]")];
    if (!tabs.length || !panels.length) return;

    const activate = (key) => {
      tabs.forEach((tab) => {
        const active = tab.dataset.gnrWinnerTab === key;
        tab.classList.toggle("is-active", active);
        tab.setAttribute("aria-selected", active ? "true" : "false");
      });
      panels.forEach((panel) => {
        const active = panel.dataset.gnrWinnerPanel === key;
        panel.classList.toggle("is-active", active);
        panel.hidden = !active;
      });
    };

    tabs.forEach((tab) => {
      tab.addEventListener("click", () => activate(tab.dataset.gnrWinnerTab));
    });
  };

  const initLottery = (root) => {
    if (root.dataset.initialized === "true") return;
    root.dataset.initialized = "true";

    initWinnersTabs(root);

    const form = root.querySelector("[data-gnr-lottery-form]");
    const error = root.querySelector("[data-gnr-error]");
    const klaviyoFormId = root.dataset.klaviyoFormId || KLAVIYO_FORM_ID;
    const wheels = [...root.querySelectorAll("[data-gnr-wheel]")];
    const emailTipDialog = root.querySelector("[data-gnr-email-tip-dialog]");
    const emailTipClose = root.querySelector("[data-gnr-email-tip-close]");
    const dialog = root.querySelector("[data-gnr-result-dialog]");
    const closeButton = root.querySelector("[data-gnr-modal-close]");
    const prizeElements = [...root.querySelectorAll("[data-gnr-prize]")];
    const copyButton = dialog?.querySelector("[data-gnr-modal-copy]");
    const copyBubble = dialog?.querySelector("[data-gnr-modal-copied]");
    let isDrawing = false;
    let hasDrawn = false;
    let wheelRotation = 0;
    let pendingResultMeta = {};
    let cachedEmail = "";
    let klaviyoSubscribed = false;
    let copyBubbleTimer = null;
    const COPY_BUBBLE_MS = 2000;

    if (!dialog || !prizeElements.length) return;

    const apiEnabled = root.dataset.lotteryApiEnabled === "true";
    const apiUrl = root.dataset.lotteryApiUrl || "https://global.shokz.com/lottery/openapi_v2";
    const actId = root.dataset.lotteryActId || "147";
    const usageScenarios = root.dataset.usageScenarios || "cs";

    const getWheelFaces = () =>
      wheels.flatMap((wheel) => [
        ...wheel.querySelectorAll(".gnr26-lottery__wheel-image, .gnr26-lottery__wheel-placeholder")
      ]);

    const setSubmitLocked = (locked) => {
      root.classList.toggle("is-submitting", locked);
      const klaviyoButton = form?.querySelector("button");
      if (!klaviyoButton) return;
      klaviyoButton.disabled = locked;
      klaviyoButton.setAttribute("aria-busy", locked ? "true" : "false");
    };

    const shakeEmailRow = () => {
      if (!form) return;
      form.classList.remove("is-shaking");
      window.requestAnimationFrame(() => {
        form.classList.add("is-shaking");
      });
      window.setTimeout(() => {
        form.classList.remove("is-shaking");
      }, 380);
    };

    const showError = (message) => {
      if (!error) return;
      error.textContent = message;
      error.hidden = false;
      shakeEmailRow();
    };

    const hideCopyBubble = () => {
      if (copyBubbleTimer) {
        window.clearTimeout(copyBubbleTimer);
        copyBubbleTimer = null;
      }
      if (copyBubble) copyBubble.hidden = true;
    };

    const showCopyBubble = () => {
      if (!copyBubble) return;
      copyBubble.hidden = false;
      if (copyBubbleTimer) window.clearTimeout(copyBubbleTimer);
      copyBubbleTimer = window.setTimeout(hideCopyBubble, COPY_BUBBLE_MS);
    };

    const copyRedeemCode = async () => {
      const codeValue = dialog.querySelector("[data-gnr-modal-code-value]");
      const code = (codeValue?.textContent || "").trim();
      if (!code) return;
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(code);
        } else {
          const input = document.createElement("textarea");
          input.value = code;
          input.setAttribute("readonly", "");
          input.style.position = "absolute";
          input.style.left = "-9999px";
          document.body.appendChild(input);
          input.select();
          document.execCommand("copy");
          document.body.removeChild(input);
        }
        showCopyBubble();
      } catch (error) {
        console.error("[gnr-lottery] copy error", error);
      }
    };

    const resolveResultKey = (resultKey) => {
      const fallback = root.dataset.previewResult || "first";
      if (Object.prototype.hasOwnProperty.call(PRIZE_ANGLES, resultKey)) {
        return resultKey;
      }
      if (Object.prototype.hasOwnProperty.call(PRIZE_ANGLES, fallback)) {
        return fallback;
      }
      return "first";
    };

    const getPrizeAngle = (resultKey) => {
      if (Object.prototype.hasOwnProperty.call(PRIZE_ANGLES, resultKey)) {
        return PRIZE_ANGLES[resultKey];
      }
      return PRIZE_ANGLES[root.dataset.previewResult] || 0;
    };

    const openResult = (resultKey, meta = {}) => {
      const key = resolveResultKey(resultKey);
      const prize = prizeElements.find((element) => element.dataset.gnrPrize === key);
      const media = dialog.querySelector("[data-gnr-modal-media]");
      const eyebrow = dialog.querySelector("[data-gnr-modal-eyebrow]");
      const title = dialog.querySelector("[data-gnr-modal-title]");
      const description = dialog.querySelector("[data-gnr-modal-description]");
      const notice = dialog.querySelector("[data-gnr-modal-notice]");
      const noticeEveryone = dialog.querySelector("[data-gnr-modal-notice-everyone]");
      const codeWrap = dialog.querySelector("[data-gnr-modal-code]");
      const codeValue = dialog.querySelector("[data-gnr-modal-code-value]");
      const cta = dialog.querySelector("[data-gnr-modal-cta]");
      const isEveryone = key === "everyone";
      const redeemCode = (root.dataset.everyoneCode || "GNRSHOKZ").trim();

      hideCopyBubble();

      media.replaceChildren();

      if (prize) {
        const source = prize.querySelector("[data-gnr-result-media]");
        if (source) media.append(source.content.cloneNode(true));
        eyebrow.textContent = prize.dataset.resultEyebrow || "";
        title.textContent = prize.dataset.resultTitle || "";
        description.textContent = prize.dataset.resultDescription || "";
      } else if (key === "none") {
        eyebrow.textContent = "";
        title.textContent = "Better luck next time!";
        description.textContent = "";
      } else {
        const fallbackPrize = prizeElements[0];
        if (!fallbackPrize) return;
        const source = fallbackPrize.querySelector("[data-gnr-result-media]");
        if (source) media.append(source.content.cloneNode(true));
        eyebrow.textContent = fallbackPrize.dataset.resultEyebrow || "";
        title.textContent = fallbackPrize.dataset.resultTitle || "";
        description.textContent = fallbackPrize.dataset.resultDescription || "";
      }

      if (notice) notice.hidden = isEveryone;
      if (noticeEveryone) noticeEveryone.hidden = !isEveryone;
      if (cta) cta.hidden = !isEveryone;

      if (codeWrap && codeValue) {
        const showCode = isEveryone && Boolean(redeemCode);
        codeValue.textContent = redeemCode;
        codeWrap.hidden = !showCode;
      }

      dialog.dataset.resultKey = key;
      openDialog(dialog);
    };

    const spinWheel = (resultKey, meta = {}) => {
      const key = resolveResultKey(resultKey);
      pendingResultMeta = meta;
      const targetAngle = getPrizeAngle(key);
      const currentMod = ((wheelRotation % 360) + 360) % 360;
      const delta = (targetAngle - currentMod + 360) % 360;

      wheelRotation += SPIN_TURNS * 360 + delta;

      getWheelFaces().forEach((face) => {
        face.style.transform = `rotate(${wheelRotation}deg)`;
      });

      wheels.forEach((wheel) => wheel.classList.add("is-spinning"));

      window.setTimeout(() => {
        wheels.forEach((wheel) => wheel.classList.remove("is-spinning"));
        isDrawing = false;
        hasDrawn = true;
        root.classList.add("is-drawn");
        setSubmitLocked(true);
        openResult(key, pendingResultMeta);
        pendingResultMeta = {};
      }, SPIN_DURATION_MS);
    };

    const requestLottery = (mbId) =>
      fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          mbId,
          actId: Number(actId) || actId,
          usageScenarios
        })
      }).then(async (res) => {
        let payload = null;
        try {
          payload = await res.json();
        } catch (parseError) {
          payload = null;
        }
        if (!res.ok) {
          const error = new Error(`HTTP error ${res.status}`);
          error.payload = payload;
          throw error;
        }
        return payload;
      });

    const parseLotteryResponse = (payload) => {
      const data = payload?.data || {};
      const outerCode = payload?.code;
      const status = pickField(data, "status");
      const actStatus = pickField(data, "act_status", "actStatus");
      const priceId = pickField(data, "priceId", "price_id");
      const code = pickField(data, "code");
      const priceName = pickField(data, "priceName", "price_name");

      const isSuccess =
        (outerCode === 200 || outerCode === "200") &&
        (status === "ok" || (priceId !== undefined && !actStatus));

      if (isSuccess) {
        return {
          ok: true,
          key: mapPriceId(priceId),
          priceId,
          code: code || "",
          priceName: priceName || ""
        };
      }

      return {
        ok: false,
        actStatus: actStatus || status || "error"
      };
    };

    const startDraw = (emailFromEvent, options = {}) => {
      const emailValue = (emailFromEvent || cachedEmail || "").trim();
      if (options.fromKlaviyo && emailValue) klaviyoSubscribed = true;
      if (isDrawing || hasDrawn || !emailValue || !klaviyoSubscribed) return;

      cachedEmail = emailValue;
      closeDialog(emailTipDialog);
      if (error) error.hidden = true;
      isDrawing = true;
      setSubmitLocked(true);

      const complete = (resultKey = root.dataset.previewResult, meta = {}) => {
        trackSubscribe("success", emailValue);
        spinWheel(resultKey, meta);
      };
      const fail = (message = MSG_SERVER_ERROR) => {
        wheels.forEach((wheel) => wheel.classList.remove("is-spinning"));
        isDrawing = false;
        setSubmitLocked(false);
        showError(message);
        trackSubscribe("fail", emailValue);
      };

      if (!apiEnabled) {
        complete(root.dataset.previewResult);
        return;
      }

      requestLottery(emailValue)
        .then((payload) => {
          const parsed = parseLotteryResponse(payload);
          if (parsed.ok) {
            complete(parsed.key, {
              priceId: parsed.priceId,
              code: parsed.code,
              priceName: parsed.priceName
            });
            return;
          }
          fail(getErrorMessage(parsed.actStatus));
        })
        .catch((err) => {
          const actStatus = pickField(err?.payload?.data, "act_status", "actStatus") || "";
          if (actStatus) {
            fail(getErrorMessage(actStatus));
            return;
          }
          fail(MSG_SERVER_ERROR);
          console.error("[gnr-lottery]", err);
        });
    };

    const closeEmailTip = () => {
      closeDialog(emailTipDialog);
      if (!klaviyoSubscribed) shakeEmailRow();
    };

    wheels.forEach((wheel) => {
      wheel.addEventListener("click", () => {
        if (isDrawing || hasDrawn) return;
        if (!klaviyoSubscribed) {
          openDialog(emailTipDialog);
          shakeEmailRow();
          return;
        }
        startDraw(cachedEmail);
      });
    });

    const api = {
      root,
      formId: klaviyoFormId,
      handleEmailSubmit: (emailValue) => startDraw(emailValue, { fromKlaviyo: true }),
      handleEmailFail: (emailValue) => {
        klaviyoSubscribed = false;
        cachedEmail = "";
        trackSubscribe("fail", (emailValue || "").trim());
        showError(MSG_SERVER_ERROR);
      }
    };
    instances.add(api);
    root._gnrLotteryApi = api;

    emailTipClose?.addEventListener("click", closeEmailTip);

    emailTipDialog?.addEventListener("click", (event) => {
      if (event.target === emailTipDialog) closeEmailTip();
    });

    closeButton?.addEventListener("click", () => closeDialog(dialog));
    dialog.addEventListener("click", (event) => {
      if (event.target === dialog) closeDialog(dialog);
    });
    dialog.addEventListener("close", hideCopyBubble);
    copyButton?.addEventListener("click", copyRedeemCode);

    const closeAllPrizeInfo = (exceptPrize) => {
      root.querySelectorAll(".gnr26-lottery__prize--first.is-info-open").forEach((prize) => {
        if (exceptPrize && prize === exceptPrize) return;
        prize.classList.remove("is-info-open");
        prize.querySelector("[data-gnr-prize-info]")?.setAttribute("aria-expanded", "false");
      });
    };

    root.querySelectorAll("[data-gnr-prize-info]").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        const prize = button.closest(".gnr26-lottery__prize");
        if (!prize) return;
        const willOpen = !prize.classList.contains("is-info-open");
        closeAllPrizeInfo(prize);
        prize.classList.toggle("is-info-open", willOpen);
        button.setAttribute("aria-expanded", willOpen ? "true" : "false");
      });
    });

    document.addEventListener("click", (event) => {
      if (!event.target.closest(".gnr26-lottery__prize--first")) {
        closeAllPrizeInfo();
      }
    });
  };

  const init = (scope = document) => {
    scope.querySelectorAll("[data-gnr-lottery]").forEach(initLottery);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init());
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", (event) => init(event.target));

  if (!window.__gnr26KlaviyoFormsBound) {
    window.__gnr26KlaviyoFormsBound = true;
    window.addEventListener("klaviyoForms", (event) => {
      const detail = event.detail || {};
      const email = detail.metaData?.$email || "";
      const matched = [];

      instances.forEach((api) => {
        if (!api?.root || !document.contains(api.root)) {
          instances.delete(api);
          return;
        }
        if (!detail.formId || !api.formId || detail.formId !== api.formId) return;
        matched.push(api);
      });

      if (!matched.length) return;

      if (detail.type === "submit") {
        matched.forEach((api) => api.handleEmailSubmit(email));
        return;
      }

      if (detail.type === "failedSubmit" || detail.type === "error") {
        matched.forEach((api) => api.handleEmailFail(email));
      }
    });
  }
})();
